import { Add as AddIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { STATUS_COLORS } from "@/theme/theme";
import { SUGGESTED_TAGS } from "../constants/tags";
import {
  useAddTagsToFlow,
  useAddTagsToTest,
  useAvailableTags,
  useRemoveTagsFromFlow,
  useRemoveTagsFromTest,
} from "../hooks/use-tags";
import type { PersistedUserFlow, Test } from "../types";
import { clickableProps } from "../utils/keyboard";
import { getTagStyle } from "../utils/tag-styles";
import { TagChip } from "./tag-chip";

// TODO: Move to types.ts
interface TestCase {
  id: string;
  name: string;
  type: "flow" | "test";
  status: string;
  priority: string;
  severity: string;
  tags?: string[];
  created_at: string;
  created_by: string;
  source?: string;
  test_id?: string;
  flow_data?: PersistedUserFlow;
  test_data?: Test;
}

export const EditableTagsCell = (params: GridRenderCellParams<TestCase>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(params.row.tags || []);
  const [newTags, setNewTags] = useState<string[]>([]);

  // Sync local state when server data changes (e.g., after navigation/refetch)
  useEffect(() => {
    setTags(params.row.tags || []);
  }, [params.row.tags]);

  // Update tags when dialog opens to show existing tags
  useEffect(() => {
    if (isEditing) {
      setNewTags(tags);
    }
  }, [isEditing, tags]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data: availableTagsData } = useAvailableTags();
  const availableTags = availableTagsData?.tags || [];

  const { mutate: addTagsToTest } = useAddTagsToTest();
  const { mutate: removeTagsFromTest } = useRemoveTagsFromTest();
  const { mutate: addTagsToFlow } = useAddTagsToFlow();
  const { mutate: removeTagsFromFlow } = useRemoveTagsFromFlow();

  const handleClose = () => {
    setIsEditing(false);
    setAnchorEl(null);
    setNewTags([]);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);

    if (params.row.type === "test" && params.row.test_data) {
      removeTagsFromTest({
        _url: `/tests/${params.row.test_data.id}/tags`,
        tags: [tagToRemove],
      } as { _url: string; tags: string[] });
    } else if (params.row.type === "flow" && params.row.flow_data) {
      removeTagsFromFlow({
        _url: `/flows/${params.row.flow_data.id}/tags`,
        tags: [tagToRemove],
      } as { _url: string; tags: string[] });
    }
  };

  const handleAutoSave = (newTagsValue: string[]) => {
    // Find tags to add (in newTagsValue but not in current tags)
    const tagsToAdd = newTagsValue.filter((tag) => !tags.includes(tag));
    // Find tags to remove (in current tags but not in newTagsValue)
    const tagsToRemove = tags.filter((tag) => !newTagsValue.includes(tag));

    // Update local state immediately for responsive UI
    setTags(newTagsValue);

    // Handle additions
    if (tagsToAdd.length > 0) {
      if (params.row.type === "test" && params.row.test_data) {
        addTagsToTest({
          _url: `/tests/${params.row.test_data.id}/tags`,
          tags: tagsToAdd,
        } as { _url: string; tags: string[] });
      } else if (params.row.type === "flow" && params.row.flow_data) {
        addTagsToFlow({
          _url: `/flows/${params.row.flow_data.id}/tags`,
          tags: tagsToAdd,
        } as { _url: string; tags: string[] });
      }
    }

    // Handle removals
    if (tagsToRemove.length > 0) {
      if (params.row.type === "test" && params.row.test_data) {
        removeTagsFromTest({
          _url: `/tests/${params.row.test_data.id}/tags`,
          tags: tagsToRemove,
        } as { _url: string; tags: string[] });
      } else if (params.row.type === "flow" && params.row.flow_data) {
        removeTagsFromFlow({
          _url: `/flows/${params.row.flow_data.id}/tags`,
          tags: tagsToRemove,
        } as { _url: string; tags: string[] });
      }
    }
  };

  // Combine available tags with suggested tags, removing duplicates
  const allSuggestedTags = [...new Set([...availableTags, ...SUGGESTED_TAGS])];
  // Don't filter out existing tags since we want to show them in the dialog
  const filteredTags = allSuggestedTags;

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "100%",
          height: "100%",
          position: "relative",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        {/* Display existing tags (up to 2, then show count) */}
        {tags.slice(0, 2).map((tag) => (
          <TagChip key={tag} tag={tag} size="small" showDeleteIcon onDelete={handleRemoveTag} />
        ))}

        {/* Show count if more than 2 tags */}
        {tags.length > 2 && (
          <TagChip
            tag={`+${tags.length - 2}`}
            size="small"
            sx={{
              backgroundColor: "grey.100",
              borderColor: "grey.500",
              color: "grey.700",
              cursor: "pointer",
            }}
            onClick={(event) => {
              event.stopPropagation();
              setIsEditing(true);
              setAnchorEl(event.currentTarget);
            }}
          />
        )}

        {/* Add button */}
        <Tooltip title="Add tags">
          <IconButton
            aria-label="Add tags"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setIsEditing(true);
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              width: 20,
              height: 20,
              color: "info.dark",
              "&:hover": {
                bgcolor: STATUS_COLORS.info.bg,
              },
            }}
          >
            <AddIcon sx={{ fontSize: "0.75rem" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            width: "400px",
            maxWidth: "90vw",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
              Add Tags
            </Typography>

            <Autocomplete
              multiple
              size="small"
              options={filteredTags}
              value={newTags}
              onChange={(_, value) => {
                setNewTags(value);
                // Auto-save when tags change
                handleAutoSave(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search or create tags..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <TagChip
                    {...getTagProps({ index })}
                    key={option}
                    tag={option}
                    size="small"
                    sx={{
                      height: 22,
                      "& .MuiChip-deleteIcon": {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                ))
              }
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const style = getTagStyle(option);
                return (
                  <Box
                    key={key}
                    component="li"
                    {...otherProps}
                    sx={{
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: style.color,
                      },
                    }}
                    {...clickableProps((e) => {
                      // Let the default behavior handle the selection
                      if (otherProps.onClick) {
                        otherProps.onClick(e as React.MouseEvent<HTMLLIElement>);
                      }
                    })}
                    onClick={(e) => {
                      // Let the default behavior handle the selection
                      if (otherProps.onClick) {
                        otherProps.onClick(e);
                      }
                    }}
                  >
                    <TagChip
                      tag={option}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        mr: 1,
                      }}
                    />
                  </Box>
                );
              }}
              freeSolo
              autoComplete
              includeInputInList
              filterSelectedOptions
              clearOnBlur
              handleHomeEndKeys
            />
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default EditableTagsCell;
